import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { useSelector } from "react-redux";
import {
  deleteImage,
  getallupdateProperties,
  updateImagesProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import successHandler from "utils/helpers/SuccessHandler";
import { useNavigate, useParams } from "react-router-dom";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import CommonLayoutV2 from "./CommonLayoutV2";
import { useDropzone } from "react-dropzone";
//@ts-ignore
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import NcImage from "shared/NcImage/NcImage";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";

export interface ImageProps {}

const Image: FC<ImageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { globalId, PropertyToEdit } = useSelector((state: RootState) => state.property);

  const [coverImage, setCoverImage] = useState<any | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<any | null>(null);
  const [placeImages, setPlaceImages] = useState<File[]>([]);
  const [reorderImages, setReorderImages] = useState<any>([]);
  const [placeImagesPreviews, setPlaceImagesPreviews] = useState<any[]>([]);
  const [propertySize, setPropertySize] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { slug } = useParams();
  const id = slug;
  const RouteID = id;
  const [isLoading, setIsLoading] = useState(true);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
  const [coverImageToDelete, setCoverImageToDelete] = useState<number | null>(null);
  const [coverImageError, setCoverImageError] = useState<string>("");
  const [placeImagesError, setPlaceImagesError] = useState<string>("");
  // ADD THIS: Cache property type in local state
  const [cachedPropertyType, setCachedPropertyType] = useState<string | null>(null);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false, fieldsCompleted: 0, totalFields: 2 },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchPropertyData();
    }
    return () => {
      mounted = false;
    };
  }, [dispatch, globalId, id]);

  const fetchPropertyData = async () => {
    if (!globalId && !id) {
      console.error("Global ID is not set");
      return;
    }

    const propertyId = id;
    const response: any = await dispatch(
      getallupdateProperties({ propertyId })
    );

    const property = response.payload?.property;

    if (property) {
      if (property.cover_image) {
        setCoverImage(property.cover_image);
        setCoverImagePreview(property.cover_image);
      }

      if (property.place_images?.length > 0) {
        const sortedPlaceImages = [...property?.place_images].sort(
          (a, b) => a.position - b.position
        );
        setPlaceImages(sortedPlaceImages);
        setPlaceImagesPreviews(sortedPlaceImages);
      }

      setPropertySize(property.property_size || null);
    } else {
      console.error("Property not found:", property);
    }
  };

  const validateImages = () => {
    const hasCoverImage = coverImage && !coverImageToDelete;
    const hasPlaceImages = placeImagesPreviews.length > 0;

    let isValid = true;
    setCoverImageError("");
    setPlaceImagesError("");
    if (!hasCoverImage) {
      setCoverImageError("Cover image is required.");
      isValid = false;
    }
    if (!hasPlaceImages) {
      setPlaceImagesError("At least one place image is required.");
      isValid = false;
    }

    return isValid;
  };

  const handleRemovePlaceImage = (index: number, id?: number) => {
    if (id) {
      setImagesToDelete((prev) => [...prev, id]);
    }
    setPlaceImages((prev) => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });

    setPlaceImagesPreviews((prev) => {
      const newPreviews = [...prev];
      newPreviews.splice(index, 1);
      return newPreviews;
    });
    setTimeout(() => {
      const hasPlaceImages = placeImagesPreviews?.length - 1 > 0;
      if (hasPlaceImages) {
        setPlaceImagesError("");
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateImages()) {
      return;
    }

    setLoading(true);

    const formData = new FormData();

    if (!globalId && !id) {
      console.error("Global ID is not set");
      return;
    }

    if (id) {
      formData.append("property[id]", id?.toString());
    }
    formData.append("property[property_size]", propertySize?.toString() || "");

    if (coverImage && !coverImage?.id) {
      formData.append("property[cover_image]", coverImage);
    }

    placeImages.forEach((image: any) => {
      if (image.id) {
        return null;
      } else {
        formData.append("property[place_images][]", image);
      }
    });

    if (reorderImages?.length > 0) {
      reorderImages?.forEach((item: any) => {
        formData.append("property[image_metadata][][blob_id]", item.blob_id);
        formData.append("property[image_metadata][][position]", item.position);
      });
    }

    const isProfileImageToUpload = placeImages?.some(
      (obj) => obj instanceof File
    );

    const isCoverImageToUpload = coverImage instanceof File;
    const allImageIds = [...imagesToDelete];
    if (coverImageToDelete) {
      allImageIds.push(coverImageToDelete);
    }

    const hasImagesToDelete = allImageIds.length > 0;
    let deletionErrors = false;

    if (hasImagesToDelete) {
      try {
        await dispatch(deleteImage({ id: allImageIds })).unwrap();
      } catch (error) {
        console.error("Error removing images:", error);
        deletionErrors = true;
      }
    }

    if (
      isProfileImageToUpload ||
      isCoverImageToUpload ||
      reorderImages?.length > 0
    ) {
      dispatch(updateImagesProperties(formData))
        .unwrap()
        .then((response) => {
          if (response?.success) {
            successHandler("Property images updated successfully!");
            navigate(`/add-listing-4/${slug}`);
          }
        })
        .catch((error) => {
          console.error("Error updating property images: ", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      if (hasImagesToDelete && !deletionErrors) {
        successHandler("Property images updated successfully!");
      }
      navigate(`/add-listing-4/${slug}`);
    }
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (typeof coverImagePreview === "string") {
        URL.revokeObjectURL(coverImagePreview);
      }
      setCoverImage(file);
      setCoverImagePreview(URL.createObjectURL(file));
      setCoverImageError("");
      setCoverImageToDelete(null);
    }
  };

  const handlePlaceImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      const validImageFiles = filesArray.filter((file) =>
        file.type.startsWith("image/")
      );

      if (validImageFiles.length > 0) {
        setPlaceImages((prev) => [...prev, ...validImageFiles]);
        setPlaceImagesPreviews((prev) => [
          ...prev,
          ...validImageFiles.map((file) => URL.createObjectURL(file)),
        ]);
        setPlaceImagesError("");
      } else {
        alert("Please select valid image files.");
      }
    }
  };

  const handleRemoveCoverImage = (id?: number) => {
    if (id) {
      setCoverImageToDelete(id);
      setCoverImage(null);
      setCoverImagePreview(null);
    } else {
      if (typeof coverImagePreview === "string") {
        URL.revokeObjectURL(coverImagePreview);
      }
      setCoverImage(null);
      setCoverImagePreview(null);
    }
    setTimeout(() => {
      setCoverImageError("");
    }, 0);
  };

  const {
    getRootProps: getCoverImageRootProps,
    getInputProps: getCoverImageInputProps,
  } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setCoverImage(file);
        setCoverImagePreview(URL.createObjectURL(file));
        setCoverImageError("");
        setCoverImageToDelete(null);
      }
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxSize: 10485760, // 10MB
  });

  const {
    getRootProps: getPlaceImagesRootProps,
    getInputProps: getPlaceImagesInputProps,
  } = useDropzone({
    onDrop: (acceptedFiles) => {
      setPlaceImages((prev) => [...prev, ...acceptedFiles]);
      const files = acceptedFiles.map((file) => URL.createObjectURL(file));
      setPlaceImagesPreviews((prevImages) => [...prevImages, ...files]);
      setPlaceImagesError("");
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxSize: 10485760, // 10MB
    multiple: true,
  });

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    const newPosition = endIndex + 1;

    setReorderImages((prev: any) => {
      const updated = prev.filter(
        (item: any) =>
          item.position !== newPosition || item.blob_id === removed.id
      );
      return [...updated, { blob_id: removed.id, position: newPosition }];
    });
    return result;
  };

  const handleDragEnd = (result: any) => {
    const { destination, source } = result;
    if (!destination) {
      return;
    }
    if (destination.index === source.index) {
      return;
    }
    const reorderedImages = reorder(
      placeImagesPreviews,
      source.index,
      destination.index
    );
    setPlaceImagesPreviews(reorderedImages);
  };

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1500);
  }, []);

  // Calculate completion for Step 3 (Images) using helper
  useEffect(() => {
    // Create a mock values object for the helper
    const imageValues = {
      cover_image: coverImage && !coverImageToDelete ? "filled" : "",
      place_images: placeImagesPreviews.length > 0 ? "filled" : "",
    };

    const requiredFields = ["cover_image", "place_images"];
    const hasErrors = !coverImage || placeImagesPreviews.length === 0;
    
    const completion = calculateStepCompletion(imageValues, requiredFields, hasErrors);

    setStepCompletionStatus((prev) => ({
      ...prev,
      3: completion,
    }));
  }, [coverImage, coverImageToDelete, placeImagesPreviews]);

  // Load completion status from PropertyToEdit using helper
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);
      
      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // ADD THIS: Update cached property type when PropertyToEdit loads
  useEffect(() => {
    if (PropertyToEdit?.property_type?.name) {
      setCachedPropertyType(PropertyToEdit.property_type.name);
    }
  }, [PropertyToEdit?.property_type?.name]);

  return (
    <CommonLayoutV2 
      currentHref="/add-listing-3" 
      PropertyID={RouteID}
      propertyType={cachedPropertyType}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Property Images
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Showcase your property with beautiful photos. High-quality images help guests visualize their stay.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Cover Image Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                1
              </span>
              Cover Image *
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
              This is the main image guests will see when browsing. Choose your best shot!
            </p>

            <div className="space-y-4">
              <div
                {...getCoverImageRootProps()}
                className="mt-1 flex justify-center px-6 py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all hover:border-[#F56948] hover:bg-orange-50 dark:hover:bg-gray-700/50 border-neutral-300 dark:border-neutral-600"
              >
                <input
                  {...getCoverImageInputProps()}
                  id="cover-image-upload"
                  name="cover-image-upload"
                  type="file"
                  className="sr-only"
                  onChange={handleCoverImageChange}
                />
                <div className="space-y-2 text-center">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-base text-neutral-600 dark:text-neutral-300 justify-center">
                    <span className="relative cursor-pointer rounded-md font-semibold text-[#F56948] hover:text-[#e55838]">
                      <span>Upload a cover image</span>
                    </span>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              </div>

              {coverImageError && (
                <div className="text-red-500 text-sm mt-2 flex items-center gap-1 ml-10">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {coverImageError}
                </div>
              )}

              {coverImagePreview && (
                <div className="mt-4">
                  <div className="relative inline-block rounded-xl overflow-hidden shadow-lg">
                    {isLoading ? (
                      <div className="w-full h-64 bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
                    ) : (
                      <>
                        <NcImage
                          src={
                            typeof coverImagePreview === "string"
                              ? coverImagePreview
                              : coverImagePreview.url
                          }
                          alt="Cover Preview"
                          className="w-full h-64 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveCoverImage(coverImagePreview?.id)
                          }
                          className="absolute top-3 right-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full p-2 shadow-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors z-10 group"
                        >
                          <svg
                            className="w-5 h-5 text-red-500 group-hover:text-red-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Place Images Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                2
              </span>
              Property Gallery *
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
              Add multiple images showcasing different areas of your property. Drag to reorder.
            </p>

            <div className="space-y-4">
              <div
                {...getPlaceImagesRootProps()}
                className="mt-1 flex justify-center px-6 py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all hover:border-[#F56948] hover:bg-orange-50 dark:hover:bg-gray-700/50 border-neutral-300 dark:border-neutral-600"
              >
                <input
                  {...getPlaceImagesInputProps()}
                  id="place-images-upload"
                  name="place-images-upload"
                  type="file"
                  className="sr-only"
                  onChange={handlePlaceImagesChange}
                  multiple
                />
                <div className="space-y-2 text-center">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                  <div className="flex text-base text-neutral-600 dark:text-neutral-300 justify-center">
                    <span className="relative cursor-pointer rounded-md font-semibold text-[#F56948] hover:text-[#e55838]">
                      <span>Upload multiple images</span>
                    </span>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    PNG, JPG, GIF up to 10MB each
                  </p>
                </div>
              </div>

              {placeImagesError && (
                <div className="text-red-500 text-sm mt-2 flex items-center gap-1 ml-10">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {placeImagesError}
                </div>
              )}

              {placeImagesPreviews.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4 ml-10">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Drag images to reorder • {placeImagesPreviews.length} {placeImagesPreviews.length === 1 ? 'image' : 'images'}
                    </span>
                  </div>

                  {isLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {Array.from({
                        length: placeImagesPreviews?.length,
                      }).map((_, previewIndex) => (
                        <div
                          key={previewIndex}
                          className="aspect-square bg-neutral-300 dark:bg-neutral-700 rounded-xl animate-pulse"
                        ></div>
                      ))}
                    </div>
                  ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable
                        droppableId="place-images"
                        direction="horizontal"
                      >
                        {(provided: any) => (
                          <div
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {placeImagesPreviews.map((preview, index) => {
                              return (
                                <Draggable
                                  key={String(preview.id || index)}
                                  draggableId={String(preview.id || index)}
                                  index={index}
                                >
                                  {(provided: any, snapshot: any) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`relative group ${snapshot.isDragging ? 'z-50' : ''}`}
                                    >
                                      <div className={`aspect-square relative rounded-xl overflow-hidden shadow-md transition-all ${snapshot.isDragging ? 'shadow-2xl scale-105 ring-2 ring-[#F56948]' : 'hover:shadow-lg'}`}>
                                        <NcImage
                                          src={
                                            typeof preview === "string"
                                              ? preview
                                              : preview.url
                                          }
                                          alt={`Gallery ${index + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all" />
                                        
                                        {/* Image Number Badge */}
                                        <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                          #{index + 1}
                                        </div>

                                        {/* Delete Button */}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemovePlaceImage(
                                              index,
                                              preview?.id
                                            )
                                          }
                                          className="absolute top-2 right-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 shadow-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                          <svg
                                            className="w-4 h-4 text-red-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M6 18L18 6M6 6l12 12"
                                            />
                                          </svg>
                                        </button>

                                        {/* Drag Handle Indicator */}
                                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <svg className="w-5 h-5 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Step 3 of 9 • Upload your property images
            </div>
            <div className="flex gap-3">
              <ButtonSecondary
                href={`/add-listing-2/${slug}`}
                disabled={loading}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Go Back
                </span>
              </ButtonSecondary>
              <ButtonPrimary 
                type="submit" 
                disabled={loading}
                className="min-w-[140px]"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                )}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </CommonLayoutV2>
  );
};

export default Image;